import { useState, useRef, useEffect } from "react";
import {
  Stack,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  MenuItem,
  Box,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";

const stop = (e) => e.stopPropagation();

// MUI's GridColumnHeaderItem attaches click/focus/blur/mousedown handlers to the
// header cell that run `currentTarget.contains(event.target)`. When our embedded
// controls (Select portal, clear button, inputs) fire those events, event.target
// isn't a valid Node relative to the header and it throws. Stopping propagation
// for all of these on the wrapper keeps the header handlers from ever running.
// (mui/mui-x #1721 / GridColumnHeaderItem `publish`.)
const stopHeaderEvents = {
  onClick: stop,
  onMouseDown: stop,
  onDoubleClick: stop,
  onKeyDown: stop,
  onFocus: stop,
  onBlur: stop,
};

// Seeds the local control value from whatever the search model holds.
function normalizeInitial(searchKind, initialValue) {
  if (searchKind === "date") {
    return { from: initialValue?.from ?? "", to: initialValue?.to ?? "" };
  }
  return initialValue ?? "";
}

// Shared small-input styling so every control lines up.
const inputSx = { "& .MuiInputBase-root": { backgroundColor: "background.paper" } };

// The status control is a `select`. A MUI FormControl (the TextField root) is
// `display: inline-flex` and shrinks to its content; `fullWidth` is meant to
// force `width: 100%`, but a Select has no intrinsic width so it collapses to
// ~the arrow. (A text input only *looks* full because <input> has a default
// ~20-char width.) Force full width on both the root and the InputBase so the
// dropdown fills its column like the text fields do.
const selectSx = {
  width: "100%",
  "& .MuiInputBase-root": {
    backgroundColor: "background.paper",
    width: "100%",
  },
};

/**
 * Column header with a compact, server-driven search control. Owns its own
 * value state (uncontrolled after mount); switching approver type / clearing all
 * remounts it via a key so the input resets. Highlights itself while a filter is
 * active.
 *
 * @param {string} label            header text
 * @param {string} field            column field key
 * @param {"text"|"date"|"status"} searchKind
 * @param {*} initialValue          seed value from the current search model
 * @param {Array} statusOptions     [{ value, label }] for status dropdowns
 * @param {Function} onChange        (field, value) => void
 */
// Icon reflecting this column's current sort state.
function SortIcon({ direction }) {
  if (direction === "asc")
    return <ArrowUpwardIcon color="primary" sx={{ fontSize: 16 }} />;
  if (direction === "desc")
    return <ArrowDownwardIcon color="primary" sx={{ fontSize: 16 }} />;
  return <UnfoldMoreIcon sx={{ fontSize: 16, color: "action.disabled" }} />;
}

export function ColumnHeaderSearch({
  label,
  field,
  searchKind,
  initialValue,
  statusOptions = [],
  onChange,
  sortDirection,
  onSortChange,
}) {
  const [value, setValue] = useState(() =>
    normalizeInitial(searchKind, initialValue)
  );
  const timer = useRef();
  useEffect(() => () => clearTimeout(timer.current), []);

  const isActive =
    searchKind === "date"
      ? Boolean(value?.from || value?.to)
      : Boolean(value && String(value).length);

  const emit = (next) => onChange(field, next);
  const emitDebounced = (next) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => emit(next), 400);
  };

  // --- text ---
  const handleText = (e) => {
    setValue(e.target.value);
    emitDebounced(e.target.value);
  };
  const handleTextKeyDown = (e) => {
    stop(e);
    if (e.key === "Enter") {
      clearTimeout(timer.current);
      emit(value);
    }
  };
  const clearText = () => {
    setValue("");
    clearTimeout(timer.current);
    emit("");
  };

  // --- status ---
  const handleStatus = (e) => {
    setValue(e.target.value);
    emit(e.target.value);
  };

  // --- date ---
  const handleDate = (key) => (e) => {
    const next = { ...value, [key]: e.target.value };
    setValue(next);
    emit(next);
  };

  return (
    <Box sx={{ width: "100%", py: 0.75 }} {...stopHeaderEvents}>
      <Stack spacing={0.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={0.5}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.25}
            sx={{ minWidth: 0 }}
          >
            <Typography
              variant="caption"
              noWrap
              sx={{
                fontWeight: 600,
                lineHeight: 1.2,
                color: isActive ? "primary.main" : "text.primary",
              }}
              title={label}
            >
              {label}
            </Typography>
            {isActive && <FilterAltIcon color="primary" sx={{ fontSize: 14 }} />}
          </Stack>
          {onSortChange && (
            <IconButton
              size="small"
              onClick={() => onSortChange(field)}
              sx={{ p: 0.25 }}
              title="Sort"
            >
              <SortIcon direction={sortDirection} />
            </IconButton>
          )}
        </Stack>

        {searchKind === "text" && (
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search"
            value={value}
            onChange={handleText}
            onKeyDown={handleTextKeyDown}
            fullWidth
            sx={inputSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: "action.active" }} />
                </InputAdornment>
              ),
              endAdornment: value ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearText} edge="end">
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        )}

        {searchKind === "status" && (
          <TextField
            select
            size="small"
            variant="outlined"
            value={value}
            onChange={handleStatus}
            fullWidth
            sx={selectSx}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {statusOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        {searchKind === "date" && (
          <Stack direction="row" spacing={0.5}>
            <TextField
              size="small"
              variant="outlined"
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={value.from}
              onChange={handleDate("from")}
              fullWidth
              sx={inputSx}
            />
            <TextField
              size="small"
              variant="outlined"
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={value.to}
              onChange={handleDate("to")}
              fullWidth
              sx={inputSx}
            />
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
